import { useCallback, useEffect, useRef, useState } from "react";
import { initializeSocket } from "../socket";
import { setCheerVideoRecording, stopMatchSounds, syncCheerClock } from "../utils/championshipCheer";
import {
  desiredTablesFromCapture,
  recordingTablesFromCapture,
} from "../utils/championshipCaptureState";

export function useChampionshipMatchSession(
  matchId,
  userId,
  onSnapshot,
  onCameras,
  deviceId = "",
  onCheer,
  onSync,
  onPointClose,
  onScoreWatch
) {
  const [socket, setSocket] = useState(null);
  const [recordingEvent, setRecordingEvent] = useState(null);
  const [tableRecording, setTableRecording] = useState(null);
  const [camerasRecording, setCamerasRecording] = useState(false);
  const [recordingTables, setRecordingTables] = useState([]);
  const [desiredTables, setDesiredTables] = useState([]);
  const [captureState, setCaptureState] = useState(null);
  const [cameraRoster, setCameraRoster] = useState(null);
  const [scoreWatchByTable, setScoreWatchByTable] = useState({});
  const onSnapshotRef = useRef(onSnapshot);
  const onCamerasRef = useRef(onCameras);
  const onCheerRef = useRef(onCheer);
  const onSyncRef = useRef(onSync);
  const onPointCloseRef = useRef(onPointClose);
  const onScoreWatchRef = useRef(onScoreWatch);
  onSnapshotRef.current = onSnapshot;
  onCamerasRef.current = onCameras;
  onCheerRef.current = onCheer;
  onSyncRef.current = onSync;
  onPointCloseRef.current = onPointClose;
  onScoreWatchRef.current = onScoreWatch;

  useEffect(() => {
    if (!matchId || !userId) return undefined;
    const token = localStorage.getItem("token");
    const sock = initializeSocket(token);
    setSocket(sock);

    const applyCaptureState = (payload) => {
      if (!payload) return;
      setCaptureState(payload);
      const recording = recordingTablesFromCapture(payload);
      const desired = desiredTablesFromCapture(payload);
      setRecordingTables(recording);
      setDesiredTables(desired);
      setCamerasRecording(recording.length > 0);
      const active =
        (payload.tables || []).find((t) => t.recording) ||
        (payload.tables || []).find((t) => t.desired) ||
        null;
      setTableRecording(
        active
          ? {
              table_number: active.table_number,
              capture_id: active.capture_id,
              media_sync_at: active.media_sync_at || null,
              cameras: active.cameras?.length,
              online: active.online_count,
              delivered: active.recording_count,
              desired: active.desired,
              recording: active.recording,
              starting: active.starting,
            }
          : null
      );
    };

    const handleSnapshot = (payload) => {
      onSnapshotRef.current?.(payload);
    };
    const handleCameras = (payload) => {
      setCameraRoster(payload || null);
      onCamerasRef.current?.(payload);
    };
    const handleRecording = (payload) => {
      // Intent-only event — truthful REC comes from cm:capture-state.
      setRecordingEvent(payload);
      if (payload?.action === "start") {
        setTableRecording((prev) => ({
          ...(prev || {}),
          table_number: payload.table_number,
          capture_id: payload.capture_id,
          media_sync_at: payload.media_sync_at || null,
          cameras: payload.cameras,
          online: payload.online,
          delivered: payload.delivered,
          desired: true,
          recording: Boolean(payload.recording),
          starting: payload.starting !== false && !payload.recording,
        }));
      } else if (payload?.action === "stop") {
        setTableRecording((prev) => {
          if (
            payload.table_number &&
            prev &&
            Number(prev.table_number) !== Number(payload.table_number)
          ) {
            return prev;
          }
          return null;
        });
      }
    };
    const handleCaptureState = (payload) => {
      applyCaptureState(payload);
    };
    const handleRecordingState = (payload) => {
      // Legacy catch-up; prefer captures if present.
      if (Array.isArray(payload?.captures)) {
        applyCaptureState({
          championship_match_id: payload.championship_match_id,
          tables: payload.captures,
          recording_tables: payload.tables,
          desired_tables: payload.desired_tables,
        });
        return;
      }
      const tables = Array.isArray(payload?.tables) ? payload.tables : [];
      setRecordingTables(
        tables.map(Number).filter((table) => Number.isFinite(table) && table > 0)
      );
      setCamerasRecording(tables.length > 0);
    };
    const handleStart = (payload) =>
      setRecordingEvent({ action: "start", ...payload });
    const handleStop = (payload) =>
      setRecordingEvent({ action: "stop", ...payload });
    const handleCheer = (payload) => {
      onCheerRef.current?.(payload);
    };
    const handleSync = (payload) => {
      onSyncRef.current?.(payload);
    };
    const handlePointClose = (payload) => {
      onPointCloseRef.current?.(payload);
    };
    const handleScoreWatch = (payload) => {
      const table = Number(payload?.table_number);
      if (!Number.isFinite(table) || table < 1) return;
      setScoreWatchByTable((prev) => ({
        ...prev,
        [table]: payload,
      }));
      onScoreWatchRef.current?.(payload);
    };

    const joinMatch = () => {
      syncCheerClock(sock);
      sock.emit(
        "cm:join",
        {
          championship_match_id: matchId,
          user_id: userId,
          ...(deviceId ? { device_id: deviceId } : {}),
        },
        (ack) => {
          if (ack?.ok && ack.data) onSnapshotRef.current?.(ack.data);
        }
      );
    };

    sock.on("cm:snapshot", handleSnapshot);
    sock.on("cm:cameras", handleCameras);
    sock.on("cm:recording", handleRecording);
    sock.on("cm:capture-state", handleCaptureState);
    sock.on("cm:recording-state", handleRecordingState);
    sock.on("cm:score-watch", handleScoreWatch);
    sock.on("cm:cheer", handleCheer);
    sock.on("cm:sync-invite", handleSync);
    sock.on("cm:sync-refuse", handleSync);
    sock.on("cm:sync-start", handleSync);
    sock.on("cm:sync-stop", handleSync);
    sock.on("cm:point-close", handlePointClose);
    sock.on("tableVideoRecordingStart", handleStart);
    sock.on("tableVideoRecordingStop", handleStop);
    sock.on("connect", joinMatch);
    joinMatch();

    return () => {
      stopMatchSounds();
      setCheerVideoRecording(false);
      sock.emit("cm:leave", {
        championship_match_id: matchId,
        user_id: userId,
        ...(deviceId ? { device_id: deviceId } : {}),
      });
      sock.off("cm:snapshot", handleSnapshot);
      sock.off("cm:cameras", handleCameras);
      sock.off("cm:recording", handleRecording);
      sock.off("cm:capture-state", handleCaptureState);
      sock.off("cm:recording-state", handleRecordingState);
      sock.off("cm:score-watch", handleScoreWatch);
      sock.off("cm:cheer", handleCheer);
      sock.off("cm:sync-invite", handleSync);
      sock.off("cm:sync-refuse", handleSync);
      sock.off("cm:sync-start", handleSync);
      sock.off("cm:sync-stop", handleSync);
      sock.off("cm:point-close", handlePointClose);
      sock.off("tableVideoRecordingStart", handleStart);
      sock.off("tableVideoRecordingStop", handleStop);
      sock.off("connect", joinMatch);
      setTableRecording(null);
      setRecordingTables([]);
      setDesiredTables([]);
      setCaptureState(null);
      setCameraRoster(null);
      setScoreWatchByTable({});
    };
  }, [matchId, userId, deviceId]);

  const emit = useCallback(
    (event, payload = {}) =>
      new Promise((resolve) => {
        if (!socket) {
          resolve({ ok: false, message: "offline" });
          return;
        }
        socket.emit(
          event,
          {
            championship_match_id: matchId,
            user_id: userId,
            ...(deviceId ? { device_id: deviceId } : {}),
            ...payload,
          },
          (ack) => resolve(ack || { ok: true })
        );
      }),
    [socket, matchId, userId, deviceId]
  );

  const ackCameraRecording = useCallback(
    async ({ captureId, cameraId, state = "recording", error = null } = {}) => {
      return emit("cm:camera-ack", {
        capture_id: captureId,
        camera_id: cameraId,
        state,
        ...(error ? { error: String(error) } : {}),
      });
    },
    [emit]
  );

  const publishScoreWatch = useCallback(
    async (payload = {}) => emit("cm:score-watch", payload),
    [emit]
  );

  useEffect(() => {
    setCheerVideoRecording(Boolean(camerasRecording));
  }, [camerasRecording]);

  return {
    socket,
    emit,
    recordingEvent,
    tableRecording,
    camerasRecording,
    recordingTables,
    desiredTables,
    captureState,
    cameraRoster,
    scoreWatchByTable,
    ackCameraRecording,
    publishScoreWatch,
  };
}
